/**
 * Host pack lobby (M8.4) — upload/select a question pack, then create a game.
 *
 * Shown to a host before a game exists (deployed mode only — the local dev server
 * has no pack store). Three single-action goals, each one control:
 *   1. **Upload** a pack file → validate on the server; a bad pack shows field-level
 *      errors (FR-8.2), a good one appears in the list.
 *   2. **Pick** one of your own packs (owner-scoped list — FR-8.4).
 *   3. **Create** a game from the selection (or the built-in demo pack). The pack is
 *      snapshotted at creation, so later edits never alter the in-flight game (FR-8.4).
 *
 * On success it calls `onGameReady(ticket)` with a freshly minted host ticket for the
 * created game; the parent then renders the live hosting UI.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { HostMode, ValidationError } from '@jeopardy/shared';
import {
  createGame,
  listPacks,
  requestHostTicketForGame,
  uploadPack,
  PackValidationError,
  type PackMeta,
  type HouseRules,
} from '../net/pack-api';
import { saveHostSession } from '../net/host-session';
import { CreateCostHint } from './CreateCostHint';

interface PackLobbyProps {
  /**
   * Called once the game is created: the freshly minted host ticket plus the game's
   * join code (which the host shares so players can join THAT game — M8).
   */
  onGameReady: (ticket: string, joinCode: string) => void;
  /** Who runs the game (#91). Defaults to `HUMAN`. */
  hostMode?: HostMode;
  /**
   * Whether the host may create a game (#171 review, PR #179). `false` only when this is a
   * paid pack game (auto-host voice) and the host is out of credits: the "Create game"
   * button is not rendered — the host tops up first. A free human-hosted pack game is never
   * gated, so this stays `true` there. Defaults to `true`.
   */
  canCreate?: boolean;
  /**
   * The host's current credit balance (#188), threaded from `CreateGame` for the paid
   * auto-host pack path. When set and `>= 1` a cost hint renders above the button; a free
   * human-hosted pack game passes `undefined` → no hint.
   */
  availableCredits?: number;
  /**
   * House Rules (#232), threaded from `CreateGame`. The resolved, bounded per-game
   * options (power-ups / buzzer / scoring); `undefined` = Classic (default), so the
   * create body is unchanged.
   */
  houseRules?: HouseRules;
}

export function PackLobby({
  onGameReady,
  hostMode = 'HUMAN',
  canCreate = true,
  availableCredits,
  houseRules,
}: PackLobbyProps): React.JSX.Element {
  const [packs, setPacks] = useState<PackMeta[]>([]);
  const [selectedPackId, setSelectedPackId] = useState<string | undefined>(undefined);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Guard against out-of-order `listPacks()` results: the mount refresh (issued when
  // the owner has no packs yet) can resolve AFTER the post-upload refresh and clobber
  // the fresh list with a stale empty one, so the just-uploaded pack "disappears"
  // (#60). Only the most-recently-issued refresh may apply its result.
  const refreshSeq = useRef(0);
  const refresh = useCallback(async () => {
    const seq = ++refreshSeq.current;
    try {
      const list = await listPacks();
      if (seq === refreshSeq.current) setPacks(list);
    } catch (e) {
      if (seq === refreshSeq.current) {
        setError(e instanceof Error ? e.message : 'Could not load your packs');
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleUpload = useCallback(
    async (file: File) => {
      setError(null);
      setValidationErrors([]);
      setBusy(true);
      try {
        const text = await file.text();
        let parsed: unknown;
        try {
          parsed = JSON.parse(text);
        } catch {
          setError('That file is not valid JSON.');
          return;
        }
        const meta = await uploadPack(parsed);
        await refresh();
        setSelectedPackId(meta.packId);
      } catch (e) {
        if (e instanceof PackValidationError) {
          setValidationErrors(e.errors);
        } else {
          setError(e instanceof Error ? e.message : 'Upload failed');
        }
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  const handleCreate = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const { gameId, joinCode } = await createGame(
        selectedPackId,
        'PACK',
        hostMode,
        houseRules,
      );
      const ticket = await requestHostTicketForGame(gameId);
      // Persist the game handle so a tab reload restores this host session instead of
      // dropping back to the lobby (the ticket is re-minted on load via the creator's
      // token — see host-session.ts + HostPage).
      saveHostSession({ gameId, joinCode });
      onGameReady(ticket, joinCode);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the game');
    } finally {
      setBusy(false);
    }
  }, [selectedPackId, onGameReady, hostMode, houseRules]);

  return (
    <div className="pack-lobby" data-testid="pack-lobby">
      <h2>Question packs</h2>

      {/* Goal: upload a pack. One control. */}
      <label className="pack-lobby__upload">
        <span>Upload a pack (JSON)</span>
        <input
          type="file"
          accept="application/json,.json"
          data-testid="pack-upload-input"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleUpload(file);
            // Reset so re-selecting the same file re-fires onChange.
            e.target.value = '';
          }}
        />
      </label>

      {validationErrors.length > 0 && (
        <div className="error-text" data-testid="pack-validation-errors">
          <p>This pack has problems — fix these and re-upload:</p>
          <ul>
            {validationErrors.map((err, i) => (
              <li key={`${err.field}-${i}`}>
                <code>{err.field}</code>: {err.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && (
        <div className="error-text" data-testid="pack-lobby-error">
          {error}
        </div>
      )}

      {/* Goal: pick a pack. One control (radio group). */}
      <fieldset className="pack-lobby__list" data-testid="pack-list">
        <legend>Choose a pack</legend>
        <label className="pack-lobby__pack">
          <input
            type="radio"
            name="pack"
            checked={selectedPackId === undefined}
            onChange={() => setSelectedPackId(undefined)}
          />
          Built-in demo pack
        </label>
        {packs.map((pack) => (
          <label className="pack-lobby__pack" key={pack.packId}>
            <input
              type="radio"
              name="pack"
              data-testid={`pack-option-${pack.packId}`}
              checked={selectedPackId === pack.packId}
              onChange={() => setSelectedPackId(pack.packId)}
            />
            {pack.name} <span className="muted">v{pack.version}</span>
          </label>
        ))}
      </fieldset>

      {/* Goal: create the game. One control — simply withdrawn when the host is out of
          credits for a paid (auto-host) pack game (#171 review, PR #179). No "add credits
          above" block: the credit slot is right above, so the extra line is just noise
          (PR #179 review). Buying a credit returns here with the button restored. */}
      {canCreate && (
        <>
          <CreateCostHint
            availableCredits={availableCredits}
            className="pack-lobby__cost"
          />
          <button
            type="button"
            className="btn btn--primary"
            data-testid="create-game"
            disabled={busy}
            onClick={() => void handleCreate()}
          >
            {busy ? 'Working…' : 'Create game'}
          </button>
        </>
      )}
    </div>
  );
}
